import fetchService from '../servises/fetch.service';
import { makeAutoObservable } from 'mobx';
import utilsService from '../servises/utils.service';
import alarmsStore from './alarms.store';
import userStore from './user.store';
import { enqueueMutation } from '../servises/mutation-queue.service';
import {
    getAccountStateGeneration,
    isAccountStateCurrent,
    registerAccountStateReset
} from '../servises/account-state.service';

class ServerStore {
    constructor() {
        makeAutoObservable(this, { tagMutationQueue: false });
    }

    serversListObj = {};
    serverCreated = null;
    serverProcesses = null;
    canAddNewServers = false;
    processesRequestSequence = 0;
    serversRequestSequence = 0;
    tagMutationQueue = new Map();

    async getServers(isForce) {
        if (!isForce && Object.keys(this.serversListObj).length > 0) {
            return;
        }
        const requestSequence = ++this.serversRequestSequence;
        const response = await fetchService.authenticatedGet(`/servers`);
        const responseData = response && response.data;
        if (!responseData || responseData.isError) {
            return;
        }
        const data = responseData.data_o === null ? [] : responseData.data_o;
        if (!Array.isArray(data)) {
            return;
        }
        if (requestSequence !== this.serversRequestSequence) {
            return;
        }

        const incomingIds = new Set(data.map(server => server._id));
        Object.keys(this.serversListObj).forEach(serverId => {
            if (!incomingIds.has(serverId)) {
                alarmsStore.removeServerAlarms(serverId);
            }
        });
        data.forEach(server => {
            this.normalizeServerData(server);
        });

        this.serversListObj = utilsService.normalizeApiData(data);
        this.serversListObj = { ...this.serversListObj };

        this.detectIfCanAddNewServers();
    }

    async getProcessesList(serverId) {
        const requestSequence = ++this.processesRequestSequence;
        const response = await fetchService.authenticatedGet(
            `/processes/${ serverId }`);
        const data = response.data.data_o;

        if (data && data.processes_list && data.processes_list.length > 0) {
            data.processes_list.forEach(process => {
                process.ramBytes = process.ram * 1024;
                const pewka = utilsService.formatBytes(process.ram * 1024);
                process.ram = `${ pewka.value } ${ pewka.unit }`;
            });
        }

        if (requestSequence === this.processesRequestSequence) {
            this.serverProcesses = data;
        }
    }

    clearProcesses() {
        this.processesRequestSequence += 1;
        this.serverProcesses = null;
    }

    clear() {
        this.serversRequestSequence += 1;
        this.processesRequestSequence += 1;
        this.serversListObj = {};
        this.serverCreated = null;
        this.serverProcesses = null;
        this.canAddNewServers = false;
        this.tagMutationQueue.clear();
    }

    async createServer(name) {
        const accountStateGeneration = getAccountStateGeneration();
        this.serversRequestSequence += 1;
        const response = await fetchService.authenticatedPost(`/servers`,
            { name: name }
        );
        if (!isAccountStateCurrent(accountStateGeneration)) {
            return null;
        }
        this.serversRequestSequence += 1;
        const responseData = response && response.data;
        const data = responseData && responseData.data_o;
        if (!responseData || responseData.isError || !data || !data._id) {
            throw new Error(responseData?.message || 'Failed to create server');
        }
        this.normalizeServerData(data);
        this.serversListObj[data._id] = data;
        this.serverCreated = data;

        this.detectIfCanAddNewServers();

        return data;
    }

    async updateServer(id, name) {
        const accountStateGeneration = getAccountStateGeneration();
        this.serversRequestSequence += 1;
        const response = await fetchService.authenticatedPost(`/server`,
            { name: name, serverId: id }
        );
        if (!isAccountStateCurrent(accountStateGeneration)) {
            return;
        }
        this.serversRequestSequence += 1;

        const data = response.data.data_o;
        this.serversListObj[data._id].name = data.name;
    }

    async updateServerTags(id, tagId, isAdd, isServerCreated) {
        const accountStateGeneration = getAccountStateGeneration();
        this.serversRequestSequence += 1;
        return enqueueMutation(this.tagMutationQueue, id, async () => {
            if (!isAccountStateCurrent(accountStateGeneration)) {
                return;
            }
            const response = await fetchService.authenticatedPost(`/server`,
                { tagId: tagId, serverId: id, isAdd }
            );
            if (!isAccountStateCurrent(accountStateGeneration)) {
                return;
            }
            this.serversRequestSequence += 1;

            const data = response.data.data_o;
            this.serversListObj[data._id].tags = data.tags;
            this.serversListObj = { ...this.serversListObj };

            if (isServerCreated && this.serverCreated) {
                this.serverCreated = data;
            }
        });
    }

    async deleteServer(serverId) {
        const accountStateGeneration = getAccountStateGeneration();
        this.serversRequestSequence += 1;
        await fetchService.authenticatedDelete(
            `/server/${ serverId }`);
        if (!isAccountStateCurrent(accountStateGeneration)) {
            return;
        }
        this.serversRequestSequence += 1;
        delete this.serversListObj[serverId];
        this.serversListObj = { ...this.serversListObj };
        alarmsStore.removeServerAlarms(serverId);
        if (this.serverCreated && this.serverCreated._id === serverId) {
            this.serverCreated = null;
        }
        this.detectIfCanAddNewServers();
    }

    detectIfCanAddNewServers() {
        const serversCount = Object.keys(this.serversListObj).length;
        const subscriptionServers = userStore.user.subscription.servers;

        if (serversCount < subscriptionServers) {
            this.canAddNewServers = true;
        } else {
            this.canAddNewServers = false;
        }
    }

    normalizeServerData(server) {
        const serverData = {};

        const metric = server.metrics ? server.metrics[0] : null;

        serverData.createdAt = utilsService.dateMe(server.createdAt);
        serverData.agentPushedTheData = !!metric;

        serverData.oldDataFetched = false;

        if (metric && metric.createdAt) {
            if (metric.createdAt + 600000 < Date.now()) {
                serverData.oldDataFetched = true;
            }
        }

        serverData.classes = '';
        if (serverData.agentPushedTheData) {
            serverData.classes = 'normal';
        } else {
            serverData.classes = 'no-data';
        }
        if (serverData.oldDataFetched) {
            serverData.classes = 'old-data';
        }

        serverData.ramDataCurrent = null;
        serverData.ramDataTotal = null;

        serverData.diskDataCurrent = null;
        serverData.diskDataTotal = null;
        serverData.gpuMemoryTotal = null;
        serverData.gpuMemoryUsed = null;

        if (serverData.agentPushedTheData) {
            serverData.timeAgo = utilsService.timeAgo(metric.createdAt);
            serverData.ramDataCurrent = utilsService.formatBytes(
                metric.ram_usage);
            serverData.ramDataTotal = utilsService.formatBytes(
                metric.ram_total);

            serverData.swapDataCurrent = utilsService.formatBytes(
                metric.swap_usage);
            serverData.swapDataTotal = utilsService.formatBytes(
                metric.swap_total);

            serverData.diskDataCurrent = utilsService.formatBytes(
                metric.disk_usage);
            serverData.diskDataTotal = utilsService.formatBytes(
                metric.disk_total);


            if(metric && metric.gpu_info){
                if(metric.gpu_info?.length){
                    if(metric.gpu_info[0].is_available){
                        const gpu = metric.gpu_info[0];
                        serverData.gpuMemoryTotal = utilsService.formatBytes(utilsService.mbToBytes(gpu.mem_total))
                        serverData.gpuMemoryUsed = utilsService.formatBytes(utilsService.mbToBytes(gpu.mem_used))
                        serverData.gpuMemoryTotalOriginal = utilsService.mbToBytes(gpu.mem_total);
                        serverData.gpuMemoryUsedOriginal = utilsService.mbToBytes(gpu.mem_used);
                    }
                }
            }
        } else {
            serverData.timeAgo = 'N/A';
        }

        server.serverData = serverData;

        alarmsStore.setAlarms(server);
    }
}

const serverStore = new ServerStore();
registerAccountStateReset(() => serverStore.clear());

export default serverStore;
